import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import EventoKPICards from './components/EventoKPICards';
import AdvancedFilters from './components/AdvancedFilters';
import AgendaView from './components/AgendaView';
import RightPanel from './components/RightPanel';
import ListaEventos from './components/ListaEventos';
import EventDetailModalNew from './components/EventDetailModalNew';
import EventFormModal from './components/EventFormModal';
import CalendarioView from './components/CalendarioView';
import { serviciosCorteService } from '../../services/serviciosCorteService';
import { employeeService } from '../../services/employeeService';

const VIEWS = [
  { key: 'agenda', label: 'Agenda', icon: 'AlignLeft' },
  { key: 'dia', label: 'Día', icon: 'CalendarCheck' },
  { key: 'semana', label: 'Semana', icon: 'CalendarDays' },
  { key: 'mes', label: 'Mes', icon: 'Calendar' },
  { key: 'lista', label: 'Lista', icon: 'List' },
];

const CalendarioEventos = () => {
  const [eventos, setEventos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [editingEvento, setEditingEvento] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('agenda');
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    clienteId: 'all',
    empleadoId: 'all',
    tipoServicio: 'all',
    estado: 'all',
    ubicacion: 'all'
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [eventosData, empleadosData] = await Promise.all([
        serviciosCorteService?.getAll(),
        employeeService?.getAll()
      ]);
      setEventos(eventosData || []);
      setEmpleados(empleadosData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const uniqueClientes = useMemo(() => {
    const clientes = eventos?.map(e => e?.cliente)?.filter(Boolean);
    return [...new Set(clientes)]?.sort();
  }, [eventos]);

  const uniqueUbicaciones = useMemo(() => {
    const ubicaciones = eventos?.map(e => e?.ubicacion)?.filter(Boolean);
    return [...new Set(ubicaciones)]?.sort();
  }, [eventos]);

  const filteredEventos = useMemo(() => {
    let filtered = [...eventos];
    if (filters?.search) {
      const s = filters?.search?.toLowerCase();
      filtered = filtered?.filter(e =>
        (e?.cliente || '')?.toLowerCase()?.includes(s) ||
        (e?.ubicacion || '')?.toLowerCase()?.includes(s) ||
        (e?.descripcionPiezas || '')?.toLowerCase()?.includes(s) ||
        (e?.observaciones || '')?.toLowerCase()?.includes(s) ||
        (e?.tipoServicio || '')?.toLowerCase()?.includes(s) ||
        (e?.estado || '')?.toLowerCase()?.includes(s)
      );
    }
    if (filters?.dateFrom) filtered = filtered?.filter(e => e?.fecha >= filters?.dateFrom);
    if (filters?.dateTo) filtered = filtered?.filter(e => e?.fecha <= filters?.dateTo);
    if (filters?.clienteId !== 'all') filtered = filtered?.filter(e => e?.cliente === filters?.clienteId);
    if (filters?.empleadoId !== 'all') filtered = filtered?.filter(e => e?.empleadoId === filters?.empleadoId);
    if (filters?.tipoServicio !== 'all') filtered = filtered?.filter(e => e?.tipoServicio === filters?.tipoServicio);
    if (filters?.estado !== 'all') filtered = filtered?.filter(e => e?.estado === filters?.estado);
    if (filters?.ubicacion !== 'all') filtered = filtered?.filter(e => e?.ubicacion === filters?.ubicacion);
    return filtered;
  }, [eventos, filters]);

  const handleFilterChange = (field, value) => {
    if (field === 'reset') {
      setFilters({ search: '', dateFrom: '', dateTo: '', clienteId: 'all', empleadoId: 'all', tipoServicio: 'all', estado: 'all', ubicacion: 'all' });
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleEventClick = (evento) => {
    setSelectedEvento(evento);
    setIsDetailModalOpen(true);
  };

  const handleCreateEvent = () => { setEditingEvento(null); setIsFormModalOpen(true); };

  const handleEditEvent = (evento) => {
    setEditingEvento(evento);
    setIsFormModalOpen(true);
    setSelectedEvento(null);
    setIsDetailModalOpen(false);
  };

  const handleSaveEvent = async (formData) => {
    try {
      if (editingEvento) {
        await serviciosCorteService?.update(editingEvento?.id, formData);
      } else {
        await serviciosCorteService?.create(formData);
      }
      await loadData();
      setIsFormModalOpen(false);
      setEditingEvento(null);
    } catch (error) {
      console.error('Error saving event:', error);
      throw error;
    }
  };

  const handleDeleteEvent = async (eventoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este evento?')) return;
    try {
      await serviciosCorteService?.delete(eventoId);
      await loadData();
      setSelectedEvento(null);
      setIsDetailModalOpen(false);
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Error al eliminar el evento');
    }
  };

  // Economic summary
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek?.setDate(now?.getDate() - now?.getDay() + 1);
  const endOfWeek = new Date(startOfWeek); endOfWeek?.setDate(startOfWeek?.getDate() + 6);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString()?.split('T')?.[0];
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)?.toISOString()?.split('T')?.[0];
  const swStr = startOfWeek?.toISOString()?.split('T')?.[0];
  const ewStr = endOfWeek?.toISOString()?.split('T')?.[0];

  const facturacionSemana = eventos?.filter(e => e?.fecha >= swStr && e?.fecha <= ewStr)?.reduce((s, e) => s + (parseFloat(e?.precioServicio) || 0), 0);
  const facturacionMes = eventos?.filter(e => e?.fecha >= startOfMonth && e?.fecha <= endOfMonth)?.reduce((s, e) => s + (parseFloat(e?.precioServicio) || 0), 0);
  const pendientesCount = eventos?.filter(e => e?.estado === 'PROGRAMADO' || e?.estado === 'Pendiente')?.length;
  const kgComprometidos = eventos?.filter(e => e?.estado !== 'CANCELADO' && e?.estado !== 'Cancelado')?.reduce((s, e) => s + (parseFloat(e?.numPiezas) || 0), 0);
  const kgEntregados = eventos?.filter(e => e?.estado === 'REALIZADO' || e?.estado === 'Finalizado')?.reduce((s, e) => s + (parseFloat(e?.numPiezas) || 0), 0);

  if (loading) {
    return (
      <>
        <Helmet><title>Calendario de Eventos - Las Dos Doncellas</title></Helmet>
        <div className="min-h-screen" style={{ background: '#111111' }}>
          <Header />
          <main className="pt-14">
            <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D6EF4E] mx-auto"></div>
                  <p className="mt-4 text-white/40 font-oranienbaum">Cargando eventos...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><title>Calendario de Eventos - Las Dos Doncellas</title></Helmet>
      <div className="min-h-screen" style={{ background: '#111111' }}>
        <Header />
        <main className="pt-14">
          <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h1 className="text-2xl sm:text-3xl font-oranienbaum font-bold" style={{ color: '#D6EF4E' }}>
                  Calendario de Eventos
                </h1>
                <p className="mt-1 text-sm text-white/40 font-oranienbaum">Centro de planificación operativa · Las Dos Doncellas</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={handleCreateEvent}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold transition-all"
                  style={{ background: '#D6EF4E', color: '#111111' }}>
                  <Icon name="Plus" size={15} />Nuevo Evento
                </button>
                <button onClick={handleCreateEvent}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold bg-[#3A7F6B]/10 text-[#3A7F6B] border border-[#3A7F6B]/30 hover:bg-[#3A7F6B]/20 transition-all">
                  <Icon name="Truck" size={15} />Nueva Entrega
                </button>
                <button onClick={handleCreateEvent}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-oranienbaum font-semibold bg-[#C9A24D]/10 text-[#C9A24D] border border-[#C9A24D]/30 hover:bg-[#C9A24D]/20 transition-all">
                  <Icon name="BookOpen" size={15} />Nueva Reserva
                </button>
                <button onClick={loadData}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-oranienbaum bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 transition-all">
                  <Icon name="RefreshCw" size={14} />
                </button>
              </div>
            </div>

            {/* KPI Cards */}
            <EventoKPICards eventos={eventos} loading={loading} />

            {/* Economic Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { label: 'Facturación Semana', value: `${facturacionSemana?.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €`, color: '#D6EF4E' },
                { label: 'Facturación Mes', value: `${facturacionMes?.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €`, color: '#D6EF4E' },
                { label: 'Eventos Pendientes', value: pendientesCount, color: '#C9A24D' },
                { label: 'Kg Comprometidos', value: `${kgComprometidos?.toLocaleString('es-ES')} kg`, color: '#3A7F6B' },
                { label: 'Kg Entregados', value: `${kgEntregados?.toLocaleString('es-ES')} kg`, color: '#3A7F6B' },
              ]?.map((item, i) => (
                <div key={i} className="bg-[#1A1A1A] rounded-xl border border-white/8 px-4 py-3 flex flex-col gap-1">
                  <span className="text-[10px] font-oranienbaum text-white/40 uppercase tracking-wide">{item?.label}</span>
                  <span className="text-lg font-oranienbaum font-bold" style={{ color: item?.color }}>{item?.value}</span>
                </div>
              ))}
            </div>

            {/* Advanced Filters */}
            <AdvancedFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              empleados={empleados}
              clientes={uniqueClientes}
              ubicaciones={uniqueUbicaciones}
            />

            {/* View switcher */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex bg-[#1A1A1A] rounded-xl border border-white/8 p-1 gap-1">
                {VIEWS?.map(v => (
                  <button key={v?.key} onClick={() => setViewMode(v?.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-semibold transition-all ${viewMode === v?.key ? 'text-[#111111]' : 'text-white/40 hover:text-white/70'}`}
                    style={viewMode === v?.key ? { background: '#D6EF4E' } : {}}>
                    <Icon name={v?.icon} size={13} />
                    {v?.label}
                  </button>
                ))}
              </div>
              <span className="text-xs text-white/30 font-oranienbaum ml-2">
                {filteredEventos?.length} evento{filteredEventos?.length !== 1 ? 's' : ''} encontrado{filteredEventos?.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Main content: calendar + right panel */}
            {viewMode === 'lista' ? (
              <ListaEventos
                eventos={filteredEventos}
                empleados={empleados}
                onEventClick={handleEventClick}
                onEdit={handleEditEvent}
                onDelete={handleDeleteEvent}
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
                {/* Main view */}
                <div className="lg:col-span-3">
                  {(viewMode === 'agenda' || viewMode === 'dia') && (
                    <AgendaView
                      eventos={filteredEventos}
                      empleados={empleados}
                      onEventClick={handleEventClick}
                    />
                  )}
                  {(viewMode === 'semana' || viewMode === 'mes') && (
                    <CalendarioView
                      eventos={filteredEventos}
                      onEventClick={handleEventClick}
                      viewMode="monthly"
                      onViewModeChange={() => {}}
                    />
                  )}
                </div>

                {/* Right panel */}
                <div className="lg:col-span-1">
                  <RightPanel eventos={eventos} onEventClick={handleEventClick} />
                </div>
              </div>
            )}

            {/* Event type legend */}
            <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-4">
              <p className="text-xs font-oranienbaum text-white/40 uppercase tracking-wide mb-3">Tipos de Evento</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'Loncheado', color: '#22c55e' },
                  { label: 'Entrega', color: '#eab308' },
                  { label: 'Comunión', color: '#3b82f6' },
                  { label: 'Boda', color: '#a855f7' },
                  { label: 'Feria', color: '#f97316' },
                  { label: 'Urgente', color: '#ef4444' },
                  { label: 'Pendiente', color: '#6b7280' },
                ]?.map(t => (
                  <div key={t?.label} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: t?.color }} />
                    <span className="text-xs font-oranienbaum text-white/50">{t?.label}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </main>

        {/* Event Form Modal */}
        <EventFormModal
          isOpen={isFormModalOpen}
          onClose={() => { setIsFormModalOpen(false); setEditingEvento(null); }}
          onSave={handleSaveEvent}
          evento={editingEvento}
          empleados={empleados}
        />

        {/* Event Detail Modal */}
        <EventDetailModalNew
          isOpen={isDetailModalOpen}
          evento={selectedEvento}
          empleados={empleados}
          onClose={() => { setIsDetailModalOpen(false); setSelectedEvento(null); }}
          onEdit={handleEditEvent}
          onDelete={handleDeleteEvent}
        />
      </div>
    </>
  );
};

export default CalendarioEventos;